import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import {
  externalAccountKeys,
  useExternalAccounts,
} from "@/features/external-accounts/hooks/use-external-accounts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const useExternalAccountsPage = () => {
  const { data: accounts, isLoading } = useExternalAccounts();
  const queryClient = useQueryClient();
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  // Check for OAuth success on mount
  useEffect(() => {
    console.log("[AccountsListPage] Checking for OAuth success flag");

    const oauthSuccess = localStorage.getItem("oauth-success-pending");
    if (oauthSuccess === "true") {
      console.log(
        "[AccountsListPage] OAuth success detected! Opening sync settings",
      );
      localStorage.removeItem("oauth-success-pending");
    }
  }, []);

  // Listen for OAuth callback messages (backup mechanism)
  useEffect(() => {
    const handleOAuthSuccess = () => {
      console.log("[AccountsListPage] Handling OAuth success");

      // Refresh accounts list
      console.log(
        "[AccountsListPage] Invalidating queries for clientId:",
        clientId,
      );
      queryClient.invalidateQueries({
        queryKey: externalAccountKeys.list(clientId),
      });
    };

    const handleMessage = (event: MessageEvent) => {
      console.log(
        "[AccountsListPage] Received message",
        event.data,
        "from",
        event.origin,
      );

      // Verify origin for security
      if (event.origin !== window.location.origin) {
        console.log("[AccountsListPage] Message origin mismatch, ignoring");
        return;
      }

      if (event.data?.type === "oauth-complete") {
        console.log(
          "[AccountsListPage] OAuth complete message received",
          event.data,
        );

        if (event.data?.success) {
          handleOAuthSuccess();
        }
      }
    };

    // Check localStorage for OAuth callback result (fallback)
    const checkLocalStorage = () => {
      const result = localStorage.getItem("oauth-callback-result");
      if (result) {
        try {
          const data = JSON.parse(result);
          console.log(
            "[AccountsListPage] Found OAuth result in localStorage",
            data,
          );

          // Only process if timestamp is recent (within last 5 seconds)
          if (data.timestamp && Date.now() - data.timestamp < 5000) {
            localStorage.removeItem("oauth-callback-result");
            if (data.success) {
              handleOAuthSuccess();
            }
          } else {
            // Clean up old data
            localStorage.removeItem("oauth-callback-result");
          }
        } catch (err) {
          console.error(
            "[AccountsListPage] Failed to parse OAuth result:",
            err,
          );
          localStorage.removeItem("oauth-callback-result");
        }
      }
    };

    // Check on mount
    checkLocalStorage();

    console.log("[AccountsListPage] Setting up message listener");
    window.addEventListener("message", handleMessage);

    // Also listen for storage events (for cross-tab communication)
    window.addEventListener("storage", checkLocalStorage);

    return () => {
      console.log("[AccountsListPage] Removing listeners");
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", checkLocalStorage);
    };
  }, [queryClient, clientId]);

  return { accounts, isLoading };
};
